import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import endpoints from './endpoints';
import { mailingRuleForm2Raw, expandMailingRule } from './helpers';
import { get, patch, post, put, MICROSERVICES } from 'api';
import { IMailingRuleState } from './interfaces';
import { IMailingRule, IExpandedMailingRule } from 'interfaces/MailingRule';
import { IMyKnownError, REQUEST_LIFE_CICLE } from 'interfaces/requests';
import {
  ICreateMailingRuleRequest,
  IUpdateMailingRuleRequest,
} from './interfaces';
import { FORM_MODE } from 'shared/enums';
import { setSelectedMailingRuleModeAction } from 'modules/mailingRuleModeNavigation';
import { openAlert } from 'modules/alertHandler';
import { parseActionForDispacth } from 'utils/helpers';

const initialState: IMailingRuleState = {
  errorMessage: '',
  loading: REQUEST_LIFE_CICLE.IDLE,
  current: undefined,
};

export const createMailingRuleAction = createAsyncThunk<
  IMailingRule,
  ICreateMailingRuleRequest,
  { rejectValue: { message: string; current: IExpandedMailingRule } }
>(
  'mailingRule/create',
  async (
    { mailingRule, resetFormSubmitting, successAction },
    { dispatch, rejectWithValue },
  ) => {
    const parsedMailingRule = mailingRuleForm2Raw(mailingRule);
    try {
      const result = await post<IMailingRule>(MICROSERVICES.STRATEGIES, {
        url: endpoints.createMailingRules(),
        data: parsedMailingRule,
      });
      dispatch(
        openAlert({
          title: 'Sucesso!',
          content: 'Régua de Mailing criada com sucesso',
          primaryButton: 'Ok',
        }),
      );
      resetFormSubmitting(false);
      dispatch(parseActionForDispacth(successAction));
      return Object.assign(result.data, { id: result.data._id });
    } catch (error) {
      const errorMessage = error.response.data.message.match('name is already')
        ? 'Já existe Regra com esse nome.'
        : 'Erro ao criar Régua de Mailing';
      dispatch(
        openAlert({
          title: 'Erro!',
          content: errorMessage,
          primaryButton: 'Ok',
        }),
      );
      console.error('error at create mailing rule', error);
      return rejectWithValue({ message: error.message, current: mailingRule });
    }
  },
);

export const showMailingRuleAction = createAsyncThunk<
  IExpandedMailingRule,
  { id: string; mode: FORM_MODE },
  { rejectValue: IMyKnownError }
>('mailingRule/show', async ({ id, mode }, { dispatch, rejectWithValue }) => {
  try {
    dispatch(clearMailingRule());
    const result = await get<IMailingRule>(MICROSERVICES.STRATEGIES, {
      url: endpoints.showMailingRule(id),
    });

    const payload = expandMailingRule(result.data);
    dispatch(setSelectedMailingRuleModeAction(mode));
    return Object.assign(payload, { id: payload._id });
  } catch (error) {
    dispatch(
      openAlert({
        title: 'Erro!',
        content: 'Erro ao visualizar Régua de Mailing selecionada!',
        primaryButton: 'Ok',
      }),
    );
    console.error('error at show mailing rule', error);
    return rejectWithValue(error.message);
  }
});

export const updateMailingRuleAction = createAsyncThunk<
  IMailingRule,
  IUpdateMailingRuleRequest,
  { rejectValue: { message: string; current: IExpandedMailingRule } }
>(
  'mailingRule/update',
  async (
    { id, mailingRule, resetFormSubmitting, successAction },
    { dispatch, rejectWithValue },
  ) => {
    const parsedMailingRule = mailingRuleForm2Raw(mailingRule);
    try {
      const result = await put<IMailingRule>(MICROSERVICES.STRATEGIES, {
        url: endpoints.updateMailingRules(id),
        data: parsedMailingRule,
      });
      dispatch(
        openAlert({
          title: 'Sucesso!',
          content: 'Régua de Mailing atualizada com sucesso',
        }),
      );
      resetFormSubmitting(false);
      dispatch(parseActionForDispacth(successAction));
      return result.data;
    } catch (error) {
      const errorMessage = error.response.data.message.match('name is already')
        ? 'Já existe Regra com esse nome.'
        : 'Erro ao atualizar Régua de Mailing';
      dispatch(
        openAlert({
          title: 'Erro!',
          content: errorMessage,
        }),
      );
      console.error('error at update mailing rule', error);
      return rejectWithValue({ message: error.message, current: mailingRule });
    }
  },
);
export const inactivateMailingRuleAction = createAsyncThunk<
  IMailingRule,
  string,
  { rejectValue: IMyKnownError }
>('mailingRule/inactivate', async (id, { rejectWithValue, dispatch }) => {
  try {
    const result = await patch<IMailingRule>(MICROSERVICES.STRATEGIES, {
      url: endpoints.inactivateMailingRule(id),
    });
    return result.data;
  } catch (error) {
    dispatch(
      openAlert({
        title: 'Erro!',
        content: 'Erro ao inativar Régua de Mailing',
        primaryButton: 'Ok',
      }),
    );
    console.error('error at inactivate mailing rule', error);
    return rejectWithValue(error.message);
  }
});

const mailingRuleSlice = createSlice({
  name: 'mailingRule',
  initialState,
  reducers: {
    clearMailingRule: () => initialState,
  },
  extraReducers: (builder) => {
    builder.addCase(createMailingRuleAction.fulfilled, (state) => {
      state.current = initialState.current;
      state.loading = REQUEST_LIFE_CICLE.SUCCEEDED;
      return state;
    });
    builder.addCase(createMailingRuleAction.pending, (state) => {
      state.loading = REQUEST_LIFE_CICLE.PENDING;
      return state;
    });
    builder.addCase(createMailingRuleAction.rejected, (state, action) => {
      state.errorMessage = action.payload?.message || '';
      state.loading = REQUEST_LIFE_CICLE.FAILED;
      state.current = action.payload?.current;
      return state;
    });

    builder.addCase(showMailingRuleAction.fulfilled, (state, action) => {
      state.current = action.payload;
      state.loading = REQUEST_LIFE_CICLE.SUCCEEDED;
      return state;
    });
    builder.addCase(showMailingRuleAction.pending, (state) => {
      state.loading = REQUEST_LIFE_CICLE.PENDING;
      return state;
    });
    builder.addCase(showMailingRuleAction.rejected, (state, action) => {
      state.errorMessage = action.payload?.message || '';
      state.loading = REQUEST_LIFE_CICLE.FAILED;
      return state;
    });

    builder.addCase(updateMailingRuleAction.fulfilled, (state) => {
      state.current = initialState.current;
      state.loading = REQUEST_LIFE_CICLE.SUCCEEDED;
      return state;
    });
    builder.addCase(updateMailingRuleAction.pending, (state) => {
      state.loading = REQUEST_LIFE_CICLE.PENDING;
      return state;
    });
    builder.addCase(updateMailingRuleAction.rejected, (state, action) => {
      state.errorMessage = action.payload?.message || '';
      state.loading = REQUEST_LIFE_CICLE.FAILED;
      state.current = action.payload?.current;
      return state;
    });

    builder.addCase(inactivateMailingRuleAction.fulfilled, (state) => {
      state.loading = REQUEST_LIFE_CICLE.SUCCEEDED;
      return state;
    });
    builder.addCase(inactivateMailingRuleAction.pending, (state) => {
      state.loading = REQUEST_LIFE_CICLE.PENDING;
      return state;
    });
    builder.addCase(inactivateMailingRuleAction.rejected, (state, action) => {
      state.errorMessage = action.payload?.message || '';
      state.loading = REQUEST_LIFE_CICLE.FAILED;
      return state;
    });
  },
});

export const { clearMailingRule } = mailingRuleSlice.actions;
export default mailingRuleSlice.reducer;
